import React, { useState, useEffect } from "react";
import "../Zone/Zone.css";
import "../Overview/Overview.css";
import ImageViewer from "../../Components/ImageViewer/ImageViewer";
import { getFileUrl } from "../../Services/storage";

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return (
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) +
    ", " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

function fmtShortDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return (
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) +
    ", " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

const STATUS_VARIANT = {
  OPEN: "bad",
  CLOSED: "ok",
  "UNDER REVIEW": "warn",
  "PENDING CLOSURE": "neutral",
};

const SOURCE_VARIANT = { public: "ok", customerSupport: "info", internal: "warn", independentEngineer: "neutral" };
const SOURCE_LABEL = { public: "Public", customerSupport: "CS", internal: "Internal", independentEngineer: "IE" };

function StorageImage({ path, style, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return;
    if (path.startsWith("http")) { setUrl(path); return; }
    getFileUrl(path).then((r) => setUrl(r.url)).catch(() => {});
  }, [path]);
  if (!url) return <div style={{ ...style, background: "var(--b1)", borderRadius: "4px" }} />;
  return <img src={url} style={style} onClick={onClick} alt="" />;
}

function Thumbnails({ images, onOpen }) {
  if (!Array.isArray(images) || !images.length) return null;
  return (
    <div style={{ display: "flex", gap: "6px", overflowX: "auto", padding: "0 2px 2px" }}>
      {images.map((src, i) => (
        <StorageImage
          key={i}
          path={src}
          onClick={() => onOpen(i)}
          style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px", flexShrink: 0, cursor: "pointer", border: "1px solid var(--b1)" }}
        />
      ))}
    </div>
  );
}

function ComplaintDetail({ className, complaint, onBack, imageOverlayOpen, onOpenImageOverlay }) {
  const [viewer, setViewer] = useState(null);

  const openViewer = (images, startIndex) => {
    setViewer({ images, startIndex });
    onOpenImageOverlay();
  };

  if (!complaint) return null;

  const status = complaint.status?.currentStatus || "OPEN";
  const statusVariant = STATUS_VARIANT[status] || "neutral";
  const source = complaint.issuedBy?.type;
  const isClosed = status === "CLOSED" || status === "PENDING CLOSURE";
  const pctLabel = complaint.location?.PCTNo
    ? `PCT-${complaint.location.PCTNo} · ${complaint.location.address || ""}`
    : complaint.location?.address || "—";

  // Turnaround: show deadline or closure time
  let turnaroundLabel = "—";
  let turnaroundVariant = "neutral";
  if (isClosed && complaint.closure?.resolvedAt) {
    turnaroundLabel = fmtDate(complaint.closure.resolvedAt);
    turnaroundVariant = "ok";
  } else if (complaint.turnaround) {
    const deadline = complaint.turnaround.toDate
      ? complaint.turnaround.toDate()
      : new Date(complaint.turnaround);
    turnaroundLabel = fmtDate(complaint.turnaround);
    turnaroundVariant = deadline < new Date() ? "bad" : "neutral";
  }

  const takenBy = complaint.takenOverBy?.phoneNumber || complaint.takenOverBy?.email || null;
  const updates = Array.isArray(complaint.latestUpdate)
    ? [...complaint.latestUpdate].reverse()
    : [];

  return (
    <div className={className} data-cy="complaint-detail-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">Complaint</div>
          <div className="subhdr-title" style={{ fontSize: "13px", fontFamily: "var(--font-mono)", letterSpacing: ".02em" }}>
            {complaint.documentId}
          </div>
        </div>
        <span className={`pill ${statusVariant}`}>
          <span className="dot" />
          {status}
        </span>
      </div>
      <div className="divider" />
      <div className="scroll">
        {/* Location */}
        <div style={{ padding: "8px 20px 0", fontSize: "var(--text-sm)", color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".03em" }}>
          {pctLabel}
        </div>

        {/* Info grid */}
        <div className="stats" style={{ gridTemplateColumns: "1fr 1fr", marginTop: "12px", marginBottom: "0" }}>
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">Category</div>
            <div style={{ fontSize: "var(--text-base)", fontWeight: 500, color: "var(--t1)", marginTop: "6px", lineHeight: 1.3 }}>
              {complaint.category || "—"}
            </div>
          </div>
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">Source</div>
            <div style={{ marginTop: "6px" }}>
              {source
                ? <span className={`snap-pill ${SOURCE_VARIANT[source] || "neutral"}`}>{SOURCE_LABEL[source] || source}</span>
                : <span style={{ fontSize: "var(--text-base)", color: "var(--t3)" }}>—</span>
              }
            </div>
          </div>
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">{isClosed ? "Resolved At" : "Deadline"}</div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: isClosed ? "#5fbe7a" : turnaroundVariant === "bad" ? "#d16a6a" : "var(--t2)", marginTop: "6px", lineHeight: 1.3 }}>
              {turnaroundLabel}
            </div>
          </div>
          <div className="stat" style={{ textAlign: "left", padding: "12px 14px" }}>
            <div className="stat-l">Taken Over By</div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--t2)", marginTop: "6px", lineHeight: 1.3, wordBreak: "break-all" }}>
              {takenBy || "—"}
            </div>
          </div>
        </div>

        {/* Complaint assets */}
        {Array.isArray(complaint.assets?.images) && complaint.assets.images.length > 0 && (
          <div style={{ padding: "12px 20px 0" }}>
            <Thumbnails
              images={complaint.assets.images}
              onOpen={(i) => openViewer(complaint.assets.images, i)}
            />
          </div>
        )}

        {/* Updates timeline */}
        {updates.length > 0 && (
          <>
            <div className="sec-hdr" style={{ marginTop: "16px" }}>
              <div className="t-section">Updates</div>
              <div className="t-label">{updates.length}</div>
            </div>
            <div className="lrows">
              {updates.map((u, i) => {
                const updater = u.updatedBy?.name || u.updatedBy?.employeeName || u.updatedBy?.phoneNumber || "—";
                const uStatus = u.currentStatus || "—";
                const uPrev = u.previousStatus;
                const uVariant = STATUS_VARIANT[uStatus] || "neutral";
                return (
                  <div key={i} className="lrow" style={{ cursor: "default", alignItems: "flex-start", gap: "10px" }}>
                    <div className="li">
                      <div className="ln" style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        {uPrev && uPrev !== uStatus && (
                          <>
                            <span style={{ fontSize: "var(--text-sm)", color: "var(--t3)" }}>{uPrev}</span>
                            <span style={{ fontSize: "var(--text-xs)", color: "var(--t3)" }}>→</span>
                          </>
                        )}
                        <span className={`snap-pill ${uVariant}`}>{uStatus}</span>
                      </div>
                      <div className="ls" style={{ marginTop: "4px" }}>{updater}</div>
                      {u.description && u.description !== "Status Changed" && (
                        <div className="ls">{u.description}</div>
                      )}
                      {Array.isArray(u.proof) && u.proof.length > 0 && (
                        <div style={{ marginTop: "6px" }}>
                          <Thumbnails
                            images={u.proof}
                            onOpen={(pi) => openViewer(u.proof, pi)}
                          />
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, fontSize: "var(--text-xs)", color: "var(--t3)", paddingTop: "2px", textAlign: "right" }}>
                      {fmtShortDate(u.updatedAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ height: "20px" }} />
      </div>

      {imageOverlayOpen && viewer && (
        <ImageViewer
          imagePaths={viewer.images}
          startIndex={viewer.startIndex}
          onClose={onBack}
        />
      )}
    </div>
  );
}

export default ComplaintDetail;
